import { useMutation } from '@apollo/client';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ADD_MODEL_PICTURE from 'api/mutations/ADD_MODEL_PICTURE';
import REMOVE_MODEL_PICTURE from 'api/mutations/REMOVE_MODEL_PICTURE';
import UPDATE_MODEL from 'api/mutations/UPDATE_MODEL';
import UPDATE_SPECIFICATION_TEAM_MEMBERS from 'api/mutations/UPDATE_SPECIFICATION_TEAM_MEMBERS';
import { dataValidationErrorHandler, registerDataValidationErrorMatcher, } from 'errors/dataValidationError';
import useDebounceMutation from 'hooks/useDebounceUpdate';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import Button from 'layout/atoms/Button/Button';
import ColorDot from 'layout/atoms/ColorDot';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Header from 'layout/atoms/Header/Header';
import Input from 'layout/atoms/Input/Input';
import InputLabel from 'layout/atoms/InputLabel/InputLabel';
import Loader from 'layout/atoms/Loader/Loader';
import Photo from 'layout/atoms/Photo/Photo';
import Textarea from 'layout/atoms/Textarea/Textarea';
import FormField from 'layout/molecules/FormField/FormField';
import Grid from 'layout/molecules/Grid/Grid';
import { useAppDispatch } from 'store';
import { openModal } from 'store/layoutSlice';
import { fetchBrands, selectBrands, selectBrandsStatus } from 'store/parameters/brandsSlice';
import { fetchCategories, selectCategories, selectCategoriesStatus, } from 'store/parameters/categoriesSlice';
import { fetchSeasons, selectSeasons, selectSeasonsStatus } from 'store/parameters/seasonsSlice';
import { fetchStatuses, selectStatuses, selectStatusesStatus, } from 'store/parameters/statusesSlice';
import { addPicture, fetchSpecificationSummaryDetails, removePicture, selectSpecificationId, selectStepValidationRequestId, updateSpecification, updateSpecificationTeamMembers, } from 'store/specificationDetailsSlice';
import { updateSpecFolderListStatus } from 'store/specificationsFoldersSlice';
import { updateSpecListStatus } from 'store/specificationsSlice';
import env from 'utils/env';
import getStatusLabel from 'utils/getStatusLabel';
import withAttachments from 'utils/graphql';
import { getStatusColorVar } from 'utils/statusColors';
import useSummaryValidation from './useSummaryValidation';
function Summary() {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { t, i18n } = useTranslation(['specification', 'common']);
    const dispatch = useAppDispatch();
    const [debounceUpdate, savingModel] = useDebounceMutation();
    const [debounceUpdateTeamMembers, savingTeamMembers] = useDebounceMutation();
    const storeData = useSelector((state) => state.specificationDetails);
    const specificationDetails = storeData === null || storeData === void 0 ? void 0 : storeData.item;
    const summaryStatus = (_a = storeData === null || storeData === void 0 ? void 0 : storeData.status) === null || _a === void 0 ? void 0 : _a.fetchSummary;
    const specificationId = useSelector(selectSpecificationId);
    const [addModelSpecificationPicture] = useMutation(ADD_MODEL_PICTURE);
    const [removeProductPicture] = useMutation(REMOVE_MODEL_PICTURE);
    const [updateModel] = useMutation(UPDATE_MODEL);
    const [updateTeamMembers] = useMutation(UPDATE_SPECIFICATION_TEAM_MEMBERS);
    const isProduction = specificationDetails.state === 'PRODUCTION';
    const { errors, markTouched, validateAll } = useSummaryValidation(specificationDetails, t);
    const stepValidationRequestId = useSelector(selectStepValidationRequestId);
    useEffect(() => {
        if (stepValidationRequestId > 0) {
            validateAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepValidationRequestId]);
    useEffect(() => {
        if (summaryStatus === 'idle' && specificationId) {
            dispatch(fetchSpecificationSummaryDetails({ specificationId }));
        }
    }, [dispatch, summaryStatus, specificationId]);
    const brands = useSelector(selectBrands);
    const brandsStatus = useSelector(selectBrandsStatus).fetch;
    const categories = useSelector(selectCategories);
    const categoriesStatus = useSelector(selectCategoriesStatus).fetch;
    const seasons = useSelector(selectSeasons);
    const seasonsStatus = useSelector(selectSeasonsStatus).fetch;
    const statuses = useSelector(selectStatuses);
    const statusesStatus = useSelector(selectStatusesStatus).fetch;
    useEffect(() => {
        if (brandsStatus === 'idle') {
            dispatch(fetchBrands());
        }
    }, [dispatch, brandsStatus]);
    useEffect(() => {
        if (categoriesStatus === 'idle') {
            dispatch(fetchCategories());
        }
    }, [dispatch, categoriesStatus]);
    useEffect(() => {
        if (seasonsStatus === 'idle') {
            dispatch(fetchSeasons());
        }
    }, [dispatch, seasonsStatus]);
    useEffect(() => {
        if (statusesStatus === 'idle') {
            dispatch(fetchStatuses());
        }
    }, [dispatch, statusesStatus]);
    useEffect(() => {
        if (!isProduction &&
            seasonsStatus === 'succeeded' &&
            !specificationDetails.season &&
            (seasons === null || seasons === void 0 ? void 0 : seasons.length)) {
            const workingSeason = seasons.find((season) => season.workingSeason);
            if (workingSeason) {
                updateModel({
                    variables: { modelSpecificationId: specificationDetails.id, seasonId: workingSeason.id },
                });
                dispatch(updateSpecification({ name: 'season', value: workingSeason }));
            }
        }
    }, [
        seasonsStatus,
        seasons,
        specificationDetails.season,
        specificationDetails.id,
        updateModel,
        dispatch,
        isProduction,
    ]);
    const handlePhotoAdd = useCallback((chosenFiles) => {
        var _a;
        withAttachments(addModelSpecificationPicture)({
            variables: { modelSpecificationId: (_a = storeData === null || storeData === void 0 ? void 0 : storeData.item) === null || _a === void 0 ? void 0 : _a.id, file: chosenFiles[0] },
        }).then((response) => {
            const { image } = response.data.addModelSpecificationPicture.modelSpecification;
            dispatch(addPicture(image));
        });
    }, [dispatch, storeData.item, addModelSpecificationPicture]);
    const handlePhotoDelete = (id) => {
        removeProductPicture({ variables: { modelSpecificationId: id } });
        dispatch(removePicture());
    };
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        dispatch(updateSpecification({ name, value }));
        if (name === 'productionTimeMinutes') {
            if (value === '') {
                return;
            }
            const parsedValue = Number(value);
            if (Number.isNaN(parsedValue) || parsedValue < 1) {
                return;
            }
            debounceUpdate(updateModel, {
                modelSpecificationId: specificationDetails.id,
                productionTimeMinutes: parsedValue,
            }, (err) => dataValidationErrorHandler(dispatch)(err));
            return;
        }
        debounceUpdate(updateModel, {
            modelSpecificationId: specificationDetails.id,
            [name]: value,
        }, (err) => dataValidationErrorHandler(dispatch)(err));
    }, [dispatch, updateModel, debounceUpdate, specificationDetails.id]);
    registerDataValidationErrorMatcher();
    const handleInputTeamMembersChange = useCallback((e) => {
        var _a, _b, _c, _d;
        const { name, value } = e.target;
        dispatch(updateSpecificationTeamMembers({ name, value }));
        debounceUpdateTeamMembers(updateTeamMembers, {
            teamMembersId: (_a = specificationDetails.specificationTeamMembersNode) === null || _a === void 0 ? void 0 : _a.id,
            adminName: ((_b = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.specificationTeamMembersNode) === null || _b === void 0 ? void 0 : _b.adminName) || '',
            designerName: ((_c = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.specificationTeamMembersNode) === null || _c === void 0 ? void 0 : _c.designerName) || '',
            patternerName: ((_d = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.specificationTeamMembersNode) === null || _d === void 0 ? void 0 : _d.patternerName) || '',
            [name]: value,
        }, () => { });
    }, [dispatch, debounceUpdateTeamMembers, updateTeamMembers, specificationDetails]);
    const handleBrandSelect = useCallback((brand) => {
        updateModel({
            variables: { modelSpecificationId: specificationDetails.id, brandId: brand.id },
        });
        dispatch(updateSpecification({ name: 'brand', value: brand }));
    }, [dispatch, updateModel, specificationDetails.id]);
    const handleCategorySelect = useCallback((category) => {
        updateModel({
            variables: { modelSpecificationId: specificationDetails.id, categoryId: category.id },
        });
        dispatch(updateSpecification({ name: 'category', value: category }));
    }, [dispatch, updateModel, specificationDetails.id]);
    const handleSeasonSelect = useCallback((season) => {
        updateModel({
            variables: { modelSpecificationId: specificationDetails.id, seasonId: season.id },
        });
        dispatch(updateSpecification({ name: 'season', value: season }));
    }, [dispatch, updateModel, specificationDetails.id]);
    const handleStatusSelect = useCallback((status) => {
        updateModel({
            variables: { modelSpecificationId: specificationDetails.id, statusId: status.id },
        });
        dispatch(updateSpecification({ name: 'status', value: status }));
        dispatch(updateSpecListStatus({ id: specificationDetails.id, status }));
        dispatch(updateSpecFolderListStatus({ id: specificationDetails.id, status }));
    }, [dispatch, updateModel, specificationDetails.id]);
    const handleStatusClear = useCallback(() => {
        updateModel({
            variables: { modelSpecificationId: specificationDetails.id, clearStatus: true },
        });
        dispatch(updateSpecification({ name: 'status', value: null }));
        dispatch(updateSpecListStatus({ id: specificationDetails.id, status: null }));
        dispatch(updateSpecFolderListStatus({ id: specificationDetails.id, status: null }));
    }, [dispatch, updateModel, specificationDetails.id]);
    const brandButtonLabel = ((_b = specificationDetails.brand) === null || _b === void 0 ? void 0 : _b.name) || t('brand');
    const categoryButtonLabel = ((_c = specificationDetails.category) === null || _c === void 0 ? void 0 : _c.name) || t('category');
    const seasonButtonLabel = ((_d = specificationDetails.season) === null || _d === void 0 ? void 0 : _d.name) || t('season');
    const statusButtonLabel = getStatusLabel(specificationDetails.status, i18n) || t('common:draft');
    if (summaryStatus === 'idle' || summaryStatus === 'loading') {
        return React.createElement(Loader, null);
    }
    return (React.createElement(Grid, { columns: 1, maxWidth: 550, gap: 24 },
        React.createElement(Photo, { size: "m", standalone: true, src: storeData.item.image ? `${env.static}/${storeData.item.image}` : null, onPhotoAdd: handlePhotoAdd, onPhotoDelete: () => { var _a; return handlePhotoDelete((_a = storeData === null || storeData === void 0 ? void 0 : storeData.item) === null || _a === void 0 ? void 0 : _a.id); }, disabled: isProduction }),
        React.createElement(Grid, { gap: 12 },
            React.createElement(Header, { size: "m" }, t('basic_informations')),
            React.createElement(Grid, { gap: 12 },
                React.createElement(FormField, { label: t('fancy_name'), error: errors.fancyName },
                    React.createElement(Input, { name: "fancyName", value: (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.fancyName) || '', onChange: handleInputChange, onBlur: () => markTouched('fancyName'), loading: savingModel, disabled: isProduction, error: Boolean(errors.fancyName) })),
                React.createElement(FormField, { label: t('item_code'), error: errors.itemCode },
                    React.createElement(Input, { name: "itemCode", value: (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.itemCode) || '', onChange: handleInputChange, onBlur: () => markTouched('itemCode'), loading: savingModel, disabled: isProduction, error: Boolean(errors.itemCode) })),
                React.createElement(FormField, { label: t('brand'), error: errors.brand },
                    React.createElement(Dropdown, { shape: "block", position: "bottomLeft", onOpen: () => markTouched('brand'), trigger: React.createElement(Button, { shape: "block", align: "spaced", theme: "border", color: errors.brand ? 'danger' : 'secondary', rightIcon: React.createElement(ChevronDownSvg, null), disabled: isProduction || brandsStatus === 'loading' }, brandButtonLabel) },
                        brands.map((brand) => {
                            var _a;
                            return (React.createElement(Button, { key: brand.id, align: "left", theme: ((_a = specificationDetails.brand) === null || _a === void 0 ? void 0 : _a.id) === brand.id ? 'fill' : 'ghost', onClick: () => handleBrandSelect(brand) }, brand.name));
                        }),
                        React.createElement("hr", null),
                        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(PlusSvg, null), onClick: () => dispatch(openModal({ name: 'brandCreator' })) }, t('add_brand')))),
                React.createElement(FormField, { label: t('category'), error: errors.category },
                    React.createElement(Dropdown, { shape: "block", position: "bottomLeft", onOpen: () => markTouched('category'), trigger: React.createElement(Button, { shape: "block", align: "spaced", theme: "border", color: errors.category ? 'danger' : 'secondary', rightIcon: React.createElement(ChevronDownSvg, null), disabled: isProduction || categoriesStatus === 'loading' }, categoryButtonLabel) },
                        categories.map((category) => {
                            var _a;
                            return (React.createElement(Button, { key: category.id, align: "left", theme: ((_a = specificationDetails.category) === null || _a === void 0 ? void 0 : _a.id) === category.id ? 'fill' : 'ghost', onClick: () => handleCategorySelect(category) }, category.name));
                        }),
                        React.createElement("hr", null),
                        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(PlusSvg, null), onClick: () => dispatch(openModal({ name: 'categoryCreator' })) }, t('add_category')))),
                React.createElement(FormField, { label: t('season'), error: errors.season },
                    React.createElement(Dropdown, { shape: "block", position: "bottomLeft", onOpen: () => markTouched('season'), trigger: React.createElement(Button, { shape: "block", align: "spaced", theme: "border", color: errors.season ? 'danger' : 'secondary', rightIcon: React.createElement(ChevronDownSvg, null), disabled: isProduction || seasonsStatus === 'loading' }, seasonButtonLabel) },
                        seasons.map((season) => {
                            var _a;
                            return (React.createElement(Button, { key: season.id, align: "left", theme: ((_a = specificationDetails.season) === null || _a === void 0 ? void 0 : _a.id) === season.id ? 'fill' : 'ghost', onClick: () => handleSeasonSelect(season) },
                                season.name,
                                season.workingSeason && (React.createElement("span", { style: {
                                        marginLeft: 8,
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: '#E6F4FF',
                                        color: '#1C7ED6',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        display: 'inline-block',
                                    } }, t('specification:working_season_current')))));
                        }),
                        React.createElement("hr", null),
                        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(PlusSvg, null), onClick: () => dispatch(openModal({ name: 'seasonCreator' })) }, t('add_season')))),
                React.createElement(FormField, { label: t('status') },
                    React.createElement(Dropdown, { shape: "block", position: "bottomLeft", trigger: React.createElement(Button, { shape: "block", align: "spaced", theme: "border", color: "secondary", leftIcon: React.createElement(ColorDot, { color: getStatusColorVar((_e = specificationDetails.status) === null || _e === void 0 ? void 0 : _e.color), draft: !specificationDetails.status }), rightIcon: React.createElement(ChevronDownSvg, null), disabled: isProduction || statusesStatus === 'loading' }, statusButtonLabel) },
                        React.createElement(Button, { align: "left", leftIcon: React.createElement(ColorDot, { draft: true }), theme: !specificationDetails.status ? 'fill' : 'ghost', onClick: handleStatusClear }, t('common:draft')),
                        statuses.length > 0 && React.createElement("hr", null),
                        statuses.map((status) => {
                            var _a;
                            return (React.createElement(Button, { key: status.id, align: "left", theme: ((_a = specificationDetails.status) === null || _a === void 0 ? void 0 : _a.id) === status.id ? 'fill' : 'ghost', leftIcon: React.createElement(ColorDot, { color: getStatusColorVar(status.color) }), onClick: () => handleStatusSelect(status) }, getStatusLabel(status, i18n)));
                        }),
                        statuses.length > 0 && React.createElement("hr", null),
                        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(PlusSvg, null), onClick: () => dispatch(openModal({ name: 'statusCreator' })) }, t('add_status')))),
                React.createElement(FormField, { label: t('production_time_minutes'), error: errors.productionTimeMinutes },
                    React.createElement(Input, { name: "productionTimeMinutes", type: "number", min: 1, required: true, value: (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.productionTimeMinutes) !== null &&
                            (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.productionTimeMinutes) !== undefined
                            ? String(specificationDetails.productionTimeMinutes)
                            : '', onChange: handleInputChange, onBlur: () => markTouched('productionTimeMinutes'), error: Boolean(errors.productionTimeMinutes), loading: savingModel, disabled: isProduction })))),
        React.createElement(Grid, { gap: 12 },
            React.createElement(Header, { size: "m" }, t('specification_description')),
            React.createElement(Textarea, { name: "description", value: (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.description) || '', rows: 5, onChange: handleInputChange, loading: savingModel, disabled: isProduction })),
        React.createElement(Grid, { gap: 12 },
            React.createElement(Header, { size: "m" }, t('team')),
            React.createElement(Grid, { gap: 12 },
                React.createElement("div", null,
                    React.createElement(InputLabel, { text: t('specification_member_ADMIN') }),
                    React.createElement(Input, { name: "adminName", value: ((_f = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.specificationTeamMembersNode) === null || _f === void 0 ? void 0 : _f.adminName) || '', onChange: handleInputTeamMembersChange, loading: savingTeamMembers, disabled: isProduction })),
                React.createElement("div", null,
                    React.createElement(InputLabel, { text: t('specification_member_DESIGNER') }),
                    React.createElement(Input, { name: "designerName", value: ((_g = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.specificationTeamMembersNode) === null || _g === void 0 ? void 0 : _g.designerName) || '', onChange: handleInputTeamMembersChange, loading: savingTeamMembers, disabled: isProduction })),
                React.createElement("div", null,
                    React.createElement(InputLabel, { text: t('specification_member_PATTERNER') }),
                    React.createElement(Input, { name: "patternerName", value: ((_h = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.specificationTeamMembersNode) === null || _h === void 0 ? void 0 : _h.patternerName) || '', onChange: handleInputTeamMembersChange, loading: savingTeamMembers, disabled: isProduction }))))));
}
export default Summary;
